import React, { PureComponent } from 'react'

import { Button, Paper, TablePagination } from '@mui/material'

import LoadingView from '../LoadingView'
import TableContent from './TableContent'
import TableToolbar from './TableToolbar'
import type { ColumnConfig } from './types'

import './Table.scss'

// MUI v4 TablePagination requires page/rowsPerPage; our props allow them optional — cast to preserve behaviour.
const AnyTablePagination = TablePagination as any

// NoInfer keeps T opt-in via <Table<Row>>: untyped usage stays `any` (no cross-config inference conflicts).
type TableProps<T = any> = {
  config: NoInfer<ColumnConfig<T>>[]
  className?: string
  count?: number
  data?: Record<string, NoInfer<T>> | NoInfer<T>[]
  error?: any
  filter?: (...args: any[]) => any
  getRowLink?: (...args: any[]) => any
  hover?: boolean
  loading?: boolean
  // Opt-in floor: table-layout is fixed, so without one narrow screens crush columns to nothing.
  minWidth?: number
  page?: number
  renderDetailView?: (...args: any[]) => any
  rowsPerPage?: number
  size?: string
  sortBy?: string
  sortOrder?: string
  title?: string
  toolbar?: React.ReactNode
  onChangePage?: (...args: any[]) => any
  onChangeRowsPerPage?: (...args: any[]) => any
  onLoadMore?: (...args: any[]) => any
  onRowClick?: (...args: any[]) => any
  onSort?: (...args: any[]) => any
}

export default class Table<T = any> extends PureComponent<TableProps<T>> {
  static defaultProps = {
    count: 0,
    className: '',
    error: '',
    hover: false,
    loading: false,
    onChangePage: () => {},
    onChangeRowsPerPage: () => {},
    onSort: () => () => {},
    page: 0,
    rowsPerPage: 25,
  }

  handleChangePage = (_e: any, page: any) => this.props.onChangePage?.(page)

  handleChangeRowsPerPage = (e: any) => this.props.onChangeRowsPerPage?.(e.target.value)

  render() {
    const {
      className,
      config,
      count,
      data,
      error,
      filter,
      getRowLink,
      onLoadMore,
      onRowClick,
      onSort,
      loading,
      hover,
      minWidth,
      page,
      rowsPerPage,
      size,
      sortOrder,
      sortBy,
      title,
      toolbar,
      renderDetailView,
    } = this.props

    const isInfinityScroll = !!onLoadMore

    return (
      <Paper>
        <div className={className}>
          {(title || filter || toolbar) && <TableToolbar filter={filter} title={title} toolbar={toolbar} />}
          <LoadingView error={error} loading={isInfinityScroll ? false : loading}>
            <TableContent
              config={config}
              data={data}
              getRowLink={getRowLink}
              hover={hover}
              minWidth={minWidth}
              onRowClick={onRowClick}
              onSort={onSort ?? (() => () => {})}
              renderDetailView={renderDetailView}
              size={size}
              sortBy={sortBy}
              sortOrder={sortOrder}
            />
          </LoadingView>
          {!!count && (
            <AnyTablePagination
              component="div"
              count={count}
              onPageChange={this.handleChangePage}
              onRowsPerPageChange={this.handleChangeRowsPerPage}
              page={page}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
            />
          )}
          {onLoadMore && (
            <Button className="table__load-more-button" disabled={loading} loading={loading} onClick={onLoadMore}>
              Load more
            </Button>
          )}
        </div>
      </Paper>
    )
  }
}
